import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, ArrowLeft, Mail, User, KeyRound, ShieldCheck, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRIMARY = '#0099FF';

const RecoverPassword = () => {
  // Step 1: request code
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  // Captcha
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  // Step 2: enter code
  const [code, setCode] = useState('');
  const [recoveredUsername, setRecoveredUsername] = useState('');
  const [needsUsername, setNeedsUsername] = useState(false);
  const [verifyUsername, setVerifyUsername] = useState('');
  // Step 3: new password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [step, setStep] = useState(1); // 1=request, 2=code, 3=confirm+password
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load captcha
  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer('');
    try {
      const res = await fetch(`${API_URL}/auth/captcha`);
      if (res.ok) {
        const data = await res.json();
        setCaptchaToken(data.token);
        setCaptchaImage(data.image);
      }
    } catch (err) {
      console.error('Error loading captcha:', err);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  // Step 1: Send recovery code
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaAnswer.trim()) {
      setError('Resuelve la verificación de seguridad');
      return;
    }
    setLoading(true);
    try {
      const body = {
        email: email.trim().toLowerCase(),
        captchaToken,
        captchaAnswer: captchaAnswer.trim(),
      };
      if (username.trim()) body.username = username.trim();

      const res = await fetch(`${API_URL}/auth/recover-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        loadCaptcha();
        throw new Error(data.message || 'Error al procesar la solicitud');
      }
      if (data.requiresUsername) setNeedsUsername(true);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Validate code — get username for confirmation
  const handleCheckCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim() || code.trim().length !== 6) {
      setError('Introduce el código de 6 dígitos');
      return;
    }
    if (needsUsername && !verifyUsername.trim()) {
      setError('Introduce tu nombre de usuario');
      return;
    }
    setLoading(true);
    try {
      const body = { code: code.trim() };
      if (needsUsername && verifyUsername.trim()) body.username = verifyUsername.trim();

      const res = await fetch(`${API_URL}/auth/recover-password/check-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Código inválido');

      // If backend says we still need username (shouldn't happen if we sent it)
      if (data.requiresUsername) {
        setNeedsUsername(true);
        setError('Introduce tu nombre de usuario para continuar');
        setLoading(false);
        return;
      }
      setRecoveredUsername(data.username);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Confirm and set new password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/recover-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), newPassword, username: recoveredUsername }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al cambiar la contraseña');
      setSuccess(data.message || 'Contraseña actualizada correctamente.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const SubmitButton = ({ children }) => (
    <button
      type="submit"
      disabled={loading}
      className="w-full py-2.5 rounded-md text-white font-medium text-sm disabled:opacity-50 transition-colors"
      style={{ backgroundColor: PRIMARY }}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      ) : children}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="ComprasSanitarias" className="h-24 w-auto mx-auto mb-3" />
          <p className="text-sm text-gray-500">Hispana de Parafarmacia S.L.</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-4">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* ── SUCCESS ── */}
          {success ? (
            <div>
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md mb-4">
                <CheckCircle size={16} className="flex-shrink-0" />
                <span className="text-sm">{success}</span>
              </div>
              <Link
                to="/login"
                className="block w-full text-center py-2.5 rounded-md text-white font-medium text-sm"
                style={{ backgroundColor: PRIMARY }}
              >
                Ir a iniciar sesión
              </Link>
            </div>

          ) : step === 1 ? (
            /* ── STEP 1: Email + optional username ── */
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Recuperar contraseña</h2>
              <p className="text-sm text-gray-500 mb-6">
                Introduce tu correo electrónico. Si recuerdas tu usuario, inclúyelo para agilizar el proceso.
              </p>
              <form onSubmit={handleRequestCode}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
                  <div className="relative">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="tu@correo.com" />
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de usuario <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <div className="relative">
                    <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                      className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Si lo recuerdas" />
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                {/* Captcha */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Verificación de seguridad</label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={16} className="text-gray-500" />
                        <span className="text-xs text-gray-500 font-medium">Resuelve la operación</span>
                      </div>
                      <button type="button" onClick={loadCaptcha} disabled={captchaLoading}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-200">
                        <RefreshCw size={14} className={captchaLoading ? 'animate-spin' : ''} />
                      </button>
                    </div>
                    {captchaImage && (
                      <div className="flex justify-center mb-3">
                        <img src={captchaImage} alt="Captcha" className="select-none pointer-events-none" draggable={false} />
                      </div>
                    )}
                    <input
                      type="text"
                      inputMode="numeric"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-center font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Tu respuesta"
                    />
                  </div>
                </div>

                <SubmitButton>Enviar código de recuperación</SubmitButton>
              </form>
            </>

          ) : step === 2 ? (
            /* ── STEP 2: Enter code ── */
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Introduce el código</h2>
              <p className="text-sm text-gray-500 mb-4">
                Revisa tu correo electrónico. Hemos enviado un código de 6 dígitos.
              </p>
              {needsUsername && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-amber-700">
                    Este correo está vinculado a varias cuentas. Introduce también tu nombre de usuario.
                    Si no lo recuerdas, contacta con <strong>pedidos@hispafarma.com</strong>.
                  </p>
                </div>
              )}
              <form onSubmit={handleCheckCode}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código de recuperación</label>
                  <div className="relative">
                    <input type="text" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-center tracking-widest font-mono text-lg"
                      placeholder="000000" maxLength={6} required />
                    <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                {needsUsername && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de usuario</label>
                    <div className="relative">
                      <input type="text" value={verifyUsername} onChange={(e) => setVerifyUsername(e.target.value)}
                        className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Tu nombre de usuario" required />
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                )}
                <SubmitButton>Verificar código</SubmitButton>
              </form>
              <button onClick={() => { setStep(1); setError(''); setCode(''); }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-3">
                Volver a solicitar código
              </button>
            </>

          ) : (
            /* ── STEP 3: Confirm user + new password ── */
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Nueva contraseña</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6">
                <p className="text-sm text-blue-800">
                  Vas a cambiar la contraseña del usuario:
                </p>
                <p className="text-lg font-bold text-blue-900 mt-1">{recoveredUsername}</p>
              </div>
              <form onSubmit={handleChangePassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
                  <div className="relative">
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Mínimo 6 caracteres" required minLength={6} />
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                  <div className="relative">
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Repite la contraseña" required minLength={6} />
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <SubmitButton>Cambiar contraseña</SubmitButton>
              </form>
              <button onClick={() => { setStep(2); setError(''); setNewPassword(''); setConfirmPassword(''); }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-3">
                Volver al código
              </button>
            </>
          )}
        </div>

        {/* Back to login */}
        <div className="text-center mt-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: PRIMARY }}>
            <ArrowLeft size={14} /> Volver a iniciar sesión
          </Link>
        </div>
        <p className="text-center text-gray-400 text-xs mt-4">&copy; 2026 Hispana de Parafarmacia S.L.</p>
      </div>
    </div>
  );
};

export default RecoverPassword;
